import { useRef, useLayoutEffect } from 'react';

const isDigit = (c) => c >= '0' && c <= '9';

/**
 * Input có giá trị hiển thị được format (chèn dấu chấm/cách, đổi hoa...) nhưng
 * KHÔNG nhảy con trỏ về cuối khi sửa giữa chuỗi.
 *
 * Cơ chế: đếm số "ký tự giá trị" (mặc định: chữ số) bên trái con trỏ trong chuỗi
 * người dùng vừa gõ, rồi đặt lại con trỏ sau đúng số ký tự giá trị đó trong chuỗi
 * đã format mới. Nhờ vậy dấu ngăn cách được thêm/bớt mà con trỏ vẫn đúng vị trí.
 *
 * @param {string|number} value   Giá trị thô do cha quản lý (controlled).
 * @param {(raw: any) => void} onChange  Nhận giá trị thô đã parse (KHÔNG phải event).
 * @param {(raw: any) => string} format  Thô -> chuỗi hiển thị.
 * @param {(typed: string) => any} parse  Chuỗi người dùng gõ -> giá trị thô.
 * @param {(char: string) => boolean} [isValueChar]  Ký tự nào được coi là "giá trị"
 *        (mặc định: chữ số). Với biến đổi chữ (toUpperCase...) truyền () => true.
 */
export default function FormattedInput({ value, onChange, format, parse, isValueChar = isDigit, ...rest }) {
    const ref = useRef(null);
    const caretRef = useRef(null);
    const display = format(value);

    // Khôi phục con trỏ ĐỒNG BỘ ngay sau khi React ghi value mới vào DOM (trước khi
    // trình duyệt vẽ) — tránh nhảy con trỏ kể cả khi gõ nhanh. rAF không đủ vì bị hoãn
    // tới frame sau, lúc đó có thể khôi phục trên state đã cũ.
    useLayoutEffect(() => {
        if (caretRef.current == null) return;
        const pos = caretRef.current;
        caretRef.current = null;
        if (ref.current && document.activeElement === ref.current) {
            ref.current.setSelectionRange(pos, pos);
        }
    });

    const handleChange = (e) => {
        const el = e.target;
        const typed = el.value;
        const caret = el.selectionStart ?? typed.length;

        let valueCharsLeft = 0;
        for (let i = 0; i < caret; i++) {
            if (isValueChar(typed[i])) valueCharsLeft++;
        }

        const raw = parse(typed);
        const newDisplay = format(raw);

        let newCaret = newDisplay.length;
        let seen = 0;
        for (let i = 0; i < newDisplay.length; i++) {
            if (seen === valueCharsLeft) {
                newCaret = i;
                break;
            }
            if (isValueChar(newDisplay[i])) seen++;
        }

        caretRef.current = newCaret;
        onChange(raw);
    };

    return <input ref={ref} value={display} onChange={handleChange} {...rest} />;
}

// ----- Presets dùng sẵn (cùng một component lõi ở trên) -----

const moneyDigits = (v) => String(v ?? '').replace(/\D/g, '');
export const formatMoney = (v) => {
    const n = Number(moneyDigits(v));
    return n > 0 ? n.toLocaleString('vi-VN') : '';
};

/** Ô nhập tiền (vi-VN). onChange trả về chuỗi chữ số thô (vd: "100000"). */
export function MoneyInput(props) {
    return (
        <FormattedInput
            inputMode="numeric"
            format={formatMoney}
            parse={moneyDigits}
            {...props}
        />
    );
}

const phoneDigits = (v) => String(v ?? '').replace(/\D/g, '').slice(0, 11);
export const formatPhone = (v) => {
    const d = phoneDigits(v);
    if (d.length <= 4) return d;
    if (d.length <= 7) return `${d.slice(0, 4)} ${d.slice(4)}`;
    if (d.length <= 10) return `${d.slice(0, 4)} ${d.slice(4, 7)} ${d.slice(7)}`;
    return `${d.slice(0, 4)} ${d.slice(4, 8)} ${d.slice(8)}`;
};

/** Ô nhập số điện thoại VN. onChange trả về chuỗi chữ số thô (vd: "0912345678"). */
export function PhoneInput(props) {
    return (
        <FormattedInput
            type="tel"
            inputMode="numeric"
            format={formatPhone}
            parse={phoneDigits}
            {...props}
        />
    );
}
